import { lazy, Suspense } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AdBanner from "@/components/feature/AdBanner";
import { useTranslation } from "react-i18next";
import HeroSlider from "./components/HeroSlider";
import TrendingNews from "./components/TrendingNews";
import LatestNews from "./components/LatestNews";
import { useSEO, SITE_URL } from "@/hooks/useSEO";
import { SectionErrorBoundary } from "@/components/base/ErrorBoundary";
import { usePublishedArticles } from "@/hooks/useArticlesStore";
import { useSiteSettings } from "@/hooks/useSiteSettings";

// Lazy-loaded secondary sections — only bundled & rendered when needed
const FeaturedDestinations = lazy(() => import("./components/FeaturedDestinations"));
const HotelsSection = lazy(() => import("./components/HotelsSection"));
const FeaturedVideos = lazy(() => import("./components/FeaturedVideos"));
const GCCBanner = lazy(() => import("./components/GCCBanner"));
const OpinionSection = lazy(() => import("./components/OpinionSection"));
const NewsCategories = lazy(() => import("./components/NewsCategories"));
const Newsletter = lazy(() => import("./components/Newsletter"));
const InfographicsSection = lazy(() => import("./components/InfographicsSection"));
const CategoryNewsSection = lazy(() => import("./components/CategoryNewsSection"));

/** Minimal skeleton shown while a lazy section's JS chunk is loading */
function SectionSkeleton({ dark = false }: { dark?: boolean }) {
  return (
    <div className={`py-16 ${dark ? "bg-[#0A1628]" : "bg-white"}`}>
      <div className="max-w-screen-xl mx-auto px-4 md:px-8">
        <div className={`h-7 w-48 rounded-lg animate-pulse mb-8 ${dark ? "bg-white/10" : "bg-gray-100"}`} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`h-48 rounded-2xl animate-pulse ${dark ? "bg-white/5" : "bg-gray-100"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const { articles: publishedArticles, loading: publishedLoading } = usePublishedArticles(true);
  const { settings } = useSiteSettings();
  const sb = settings?.sectionBindings || {};

  useSEO({
    title: t("seo.home.title"),
    description: t("seo.home.description"),
    keywords: t("seo.home.keywords"),
    canonical: "/",
    ogImage: "https://static.readdy.ai/image/433f60a06dda16b0e3fd209711ce4024/45c8d4d7d672c9e272b7c78f92025684.png",
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "NewsMediaOrganization",
        "name": t("brand.fullName"),
        "alternateName": "Al Siyahiah",
        "url": SITE_URL,
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/433f60a06dda16b0e3fd209711ce4024/45c8d4d7d672c9e272b7c78f92025684.png"
        },
        "description": t("seo.home.description"),
        "foundingDate": "2011",
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "info@alsiyahiah.com",
          "contactType": "customer service"
        },
        "sameAs": [
          "https://twitter.com/alsiyahiah",
          "https://www.instagram.com/alsiyahiah"
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": t("brand.name"),
        "url": SITE_URL,
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${SITE_URL}/category/{search_term_string}`
          },
          "query-input": "required name=search_term_string"
        }
      }
    ],
  });

  return (
    <div className="font-['Cairo'] bg-white" dir="rtl">
      <Navbar />
      <main>
        <SectionErrorBoundary>
          <HeroSlider key={`hero-${sb.heroSlider || "default"}`} bindingSlug={sb.heroSlider || ""} />
        </SectionErrorBoundary>

        {/* ── Leaderboard Ad — above trending/most-read section ── */}
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 pt-6 pb-2">
          <AdBanner placementId="home-trending-top" size="leaderboard" />
        </div>

        <SectionErrorBoundary>
          <TrendingNews />
        </SectionErrorBoundary>

        <SectionErrorBoundary>
          <LatestNews key={`latest-${sb.latestNews || "default"}`} articles={publishedArticles} loading={publishedLoading} bindingSlug={sb.latestNews || ""} />
        </SectionErrorBoundary>

        {/* ── Leaderboard Ad — between latest news & destinations ── */}
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-4">
          <AdBanner placementId="home-leaderboard-1" size="leaderboard" />
        </div>

        <SectionErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <FeaturedDestinations
              key={`dest-${sb.featuredDestinations || "destinations"}`}
              articles={publishedArticles}
              loading={publishedLoading}
              bindingSlug={sb.featuredDestinations || "destinations"}
            />
          </Suspense>
        </SectionErrorBoundary>

        <SectionErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <HotelsSection
              key={`hotels-${sb.hotelsSection || "hotels"}`}
              articles={publishedArticles}
              loading={publishedLoading}
              bindingSlug={sb.hotelsSection || "hotels"}
            />
          </Suspense>
        </SectionErrorBoundary>

        {/* ── Inline Ad ── */}
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 pb-4">
          <AdBanner placementId="home-inline-1" size="inline" />
        </div>

        <SectionErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <CategoryNewsSection
              key={`aviation-${sb.aviation || "aviation"}`}
              sectionKey="aviation"
              bindingSlug={sb.aviation || "aviation"}
              title={t("home.aviation.title")}
              subtitle={t("home.aviation.subtitle")}
              accentColor="#0A4D8C"
              bgColor="bg-[#F8F9FA]"
              layout="grid-3"
              maxArticles={3}
            />
          </Suspense>
        </SectionErrorBoundary>

        <SectionErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <CategoryNewsSection
              key={`tips-${sb.travelTips || "travel-tips"}`}
              sectionKey="travel-tips"
              bindingSlug={sb.travelTips || "travel-tips"}
              title={t("home.travelTips.title")}
              subtitle={t("home.travelTips.subtitle")}
              accentColor="#D4AF37"
              bgColor="bg-white"
              layout="grid-4"
              maxArticles={4}
            />
          </Suspense>
        </SectionErrorBoundary>

        <SectionErrorBoundary>
          <Suspense fallback={<SectionSkeleton dark />}>
            <FeaturedVideos />
          </Suspense>
        </SectionErrorBoundary>

        <SectionErrorBoundary>
          <Suspense fallback={<div className="py-8" />}>
            <GCCBanner />
          </Suspense>
        </SectionErrorBoundary>

        <SectionErrorBoundary>
          <Suspense fallback={<SectionSkeleton dark />}>
            <OpinionSection />
          </Suspense>
        </SectionErrorBoundary>

        <SectionErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <NewsCategories key={`ncat-${sb.newsCategories || "default"}`} bindingSlug={sb.newsCategories || ""} />
          </Suspense>
        </SectionErrorBoundary>

        <SectionErrorBoundary>
          <Suspense fallback={<div className="py-8" />}>
            <Newsletter />
          </Suspense>
        </SectionErrorBoundary>

        <SectionErrorBoundary>
          <Suspense fallback={<SectionSkeleton />}>
            <InfographicsSection />
          </Suspense>
        </SectionErrorBoundary>
      </main>
      <Footer />
    </div>
  );
}
